import {useMutation} from '@apollo/client'
import {useMemo, useRef} from 'react'
import {parseCookies} from 'nookies'
import {isNil, omitBy, pick} from 'lodash-es'
import {FAKE_ADDRESS} from 'modules/checkout'
import {useRouter} from '_app/hooks/useRouter'
import {SET_PAYMENT_METHOD_ON_CART} from '_app/graphql/mutations/cart/SetPaymentMethodOnCart'
import useCart from '_app/hooks/useCart'
import {useGetCartPricesQuery, useGetMerchantSessionLazyQuery, usePlaceOrderMutation,} from '_app/types/api'
import getLocale from '_app/utils/localized/getLocale'
import logger from '_app/utils/logger'
import toast from '_app/utils/toast'
import {APP_ROUTES} from '_app/routes'
import {useUpdateUserCart} from '_app/hooks/useUpdateUserCart'
import {APPLE_PAY_API_VERSION, PICKUP_ALLOWED_METHODS} from './constants'
import {filterAllowedMethods, getRequiredShippingContactFields, prepareShippingAddress,} from './utils'

type Props = {
    modalCloseCallback?: () => void;
};

export const useAppleSession = ({modalCloseCallback}: Props) => {
    const {locale, push} = useRouter()
    const {data} = useCart()
    const {cartId} = parseCookies()
    const [setPaymentMethodOnCart] = useMutation(SET_PAYMENT_METHOD_ON_CART)
    const [placeOrderMutation] = usePlaceOrderMutation()
    const {
        setBillingAddressOnCart,
        setShippingAddressOnCart,
        setGuestEmailOnCart,
        setShippingMethodsOnCart,
    } = useUpdateUserCart()
    const [getMerchantSessionQuery] = useGetMerchantSessionLazyQuery()
    const {data: cartPrices} = useGetCartPricesQuery({
        variables: {cartId},
    })
    const total = useRef({label: '', amount: '0'})
    const selectedMethod =
        data?.cart?.shipping_addresses?.[0]?.selected_shipping_method?.carrier_code
    const isPickupMethod = PICKUP_ALLOWED_METHODS.includes(
        selectedMethod as typeof PICKUP_ALLOWED_METHODS[number]
    )
    const availableShippingMethods =
        data?.cart?.shipping_addresses?.[0]?.available_shipping_methods || []

    const applePayOptions: ApplePayJS.ApplePayPaymentRequest = useMemo(() => {
        const shippingMethods = availableShippingMethods
            .filter(filterAllowedMethods(locale, selectedMethod))
            .map(method => ({
                label: method.carrier_title,
                amount: method.amount.value?.toString() || '0',
                detail:
                    method.method_title?.replace(method.carrier_title, '').trim() || '',
                identifier: method.carrier_code,
            }))

        total.current = {
            label: locale === 'de' ? 'Lilou GmbH' : 'Lilou Online Shop Sp. z o.o.',
            amount: cartPrices?.cart?.prices?.grand_total?.value?.toString() || '0',
        }

        return {
            countryCode: data?.cart?.shipping_addresses[0]?.country?.code || 'PL',
            currencyCode: cartPrices?.cart?.prices?.grand_total?.currency || 'PLN',
            merchantCapabilities: ['supports3DS'],
            supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
            shippingMethods,
            requiredBillingContactFields: ['name'],
            requiredShippingContactFields:
                getRequiredShippingContactFields(isPickupMethod),
            total: total.current,
        }
    }, [data, locale, cartPrices, isPickupMethod, selectedMethod])

    const getTotal = (additionalAmount = '0') => {
        const newAmount = Number(total.current.amount) + Number(additionalAmount)
        total.current = {...total.current, amount: newAmount.toString()}

        return total.current
    }

    const updateUserDetails = async (
        contact: ApplePayJS.ApplePayPaymentContact
    ) => {
        const contactData = {
            city: contact.locality,
            postcode: contact.postalCode,
            firstname: contact.givenName,
            lastname: contact.familyName,
            street: contact.addressLines,
            telephone: contact.phoneNumber,
            country_code: contact.countryCode || getLocale(locale).toUpperCase(),
        }
        const address = {
            ...FAKE_ADDRESS,
            ...omitBy(contactData, isNil),
        }


        const shippingAddress = prepareShippingAddress(
            data,
            isPickupMethod
                ? pick(address, ['firstname', 'lastname', 'telephone'])
                : address
        )

        const billingAddressPromise = setBillingAddressOnCart(address)
        const shippingAddressPromise = setShippingAddressOnCart(shippingAddress)
        const emailPromise =
            contact.emailAddress && setGuestEmailOnCart(contact.emailAddress)

        return Promise.all([
            billingAddressPromise,
            shippingAddressPromise,
            emailPromise,
        ])
    }

    const getMerchantSession = async () => {
        try {
            const merchantSession = await getMerchantSessionQuery()
            const merchantObjectString = merchantSession.data?.AppleGraphql?.appleObj
            if (!merchantObjectString) {
                throw new Error('Invalid merchant object.')
            }
            const merchantObject = JSON.parse(merchantObjectString)

            return merchantObject
        } catch (e) {
            logger.error(e)
            toast(
                new Error('Could not get merchant session. Please try again later.')
            )
        }
    }

    const setPaymentMethod = async () => {
        await setPaymentMethodOnCart({
            variables: {
                paymentMethod: {
                    code: 'idea_applepay',
                },
            },
        })
    }

    const updateOrder = async (
        event: ApplePayJS.ApplePayPaymentAuthorizedEvent
    ) => {
        if (event.payment.shippingContact) {
            await Promise.all([
                updateUserDetails(event.payment.shippingContact),
                setPaymentMethod(),
            ])
        } else {
            throw new Error('Missing shipping contact data')
        }
    }

    const placeOrder = async (
        event: ApplePayJS.ApplePayPaymentAuthorizedEvent
    ) => {
        try {
            const token = event.payment.token
            const stringifiedToken = JSON.stringify(token, null, 2)

            const placeOrderResult = await placeOrderMutation({
                variables: {
                    cartId,
                    applePayToken: stringifiedToken,
                },
            })

            return placeOrderResult.data?.placeOrder?.order?.order_number
        } catch (e) {
            logger.error(e)
            toast(new Error('Place order failed. Please try again later.'))
        }
    }

    const startAppleSession = () => {
        const session = new window.ApplePaySession(APPLE_PAY_API_VERSION, applePayOptions)
        session.onvalidatemerchant = async () => {
            const merchantSession = await getMerchantSession()
            session.completeMerchantValidation(merchantSession)
        }
        session.onpaymentmethodselected = () => {
            const update = {
                newTotal: getTotal(),
            }
            session.completePaymentMethodSelection(update)
        }
        session.onshippingmethodselected = async event => {
            await setShippingMethodsOnCart({
                carrier_code: event.shippingMethod.identifier,
            })
            const update = {
                newTotal: getTotal(event.shippingMethod.amount),
            }
            session.completeShippingMethodSelection(update)
        }
        session.onshippingcontactselected = () => {
            const update = {
                newTotal: getTotal(),
            }
            session.completeShippingContactSelection(update)
        }
        session.onpaymentauthorized = async event => {
            await updateOrder(event)
            const orderNumber = await placeOrder(event)
            if (orderNumber) {
                session.completePayment({
                    status: ApplePaySession.STATUS_SUCCESS,
                })
                await push({
                    pathname: APP_ROUTES.CHECKOUT_SUCCESS,
                    query: {
                        order_id: orderNumber,
                    },
                })
            } else {
                session.completePayment({
                    status: ApplePaySession.STATUS_FAILURE,
                })
            }
        }
        session.oncancel = event => {
            if (modalCloseCallback) {
                modalCloseCallback()
            }
            logger.info('Apple Session cancelled', event)
        }
        session.begin()
    }
    return {startAppleSession}
}
